<template>
  <md-card>
    <md-card-header class="md-card-header-icon md-card-header-blue">
      <div class="card-icon">
        <md-icon>flight_takeoff</md-icon>
      </div>
      <h4 class="title">
        Departure Info
      </h4>
    </md-card-header>
    <md-card-content>
      <div class="md-layout">
        <div class="md-layout-item md-size-100">
          <span class="md-caption text-warning">
            This is {{ tourTypeDescription }}
          </span>
          <h6>Travel Dates</h6>
        </div>
        <div class="md-layout-item md-size-50">
          <md-datepicker
            v-if="$allowed('departure.manage')"
            v-model="start_date"
            md-immediately
            :md-model-type="String"
          >
            <label>Departure Date</label>
          </md-datepicker>
          <md-field v-else class="md-form-group" slot="inputs">
            <md-icon class="text-primary hi-price-icon">event</md-icon>
            <label>Departure Date</label>
            <md-input
              :readonly="!$allowed('departure.manage')"
              v-model="start_date"
              type="text"
            ></md-input>
          </md-field>
        </div>
        <div class="md-layout-item md-size-50">
          <md-datepicker
            v-if="$allowed('departure.manage')"
            v-model="end_date"
            md-immediately
            :md-model-type="String"
          >
            <label>Return Date</label>
          </md-datepicker>
          <md-field v-else class="md-form-group" slot="inputs">
            <md-icon class="text-primary hi-price-icon">event</md-icon>
            <label>Return Date</label>
            <md-input
              :readonly="!$allowed('departure.manage')"
              v-model="end_date"
              type="text"
            ></md-input>
          </md-field>
        </div>
      </div>

      <div
        v-if="$allowed('departure.manage') && tourType == 'tour'"
        class="md-layout"
      >
        <div class="md-layout-item md-size-50">
          <h6>Prices</h6>
        </div>
        <div class="md-layout-item md-size-50 text-right">
          <md-switch v-model="price_sync">Synced with Adventure</md-switch>
        </div>
        <div class="md-layout-item md-size-33 price-row">
          <md-field class="md-form-group" slot="inputs">
            <md-icon
              ><i class="fas fa-pound-sign text-primary hi-price-icon"></i
            ></md-icon>
            <label>GBP</label>
            <md-input
              :disabled="price_sync"
              v-model="gbp"
              type="text"
            ></md-input>
          </md-field>
        </div>
        <div class="md-layout-item md-size-33 price-row">
          <md-field class="md-form-group" slot="inputs">
            <md-icon
              ><i class="fas fa-euro-sign text-primary hi-price-icon"></i
            ></md-icon>
            <label>EUR</label>
            <md-input
              :disabled="price_sync"
              v-model="eur"
              type="text"
            ></md-input>
          </md-field>
        </div>
        <div class="md-layout-item md-size-33 price-row">
          <md-field class="md-form-group" slot="inputs">
            <md-icon
              ><i class="fas fa-dollar-sign text-primary hi-price-icon"></i
            ></md-icon>
            <label>USD</label>
            <md-input
              :disabled="price_sync"
              v-model="usd"
              type="text"
            ></md-input>
          </md-field>
        </div>
      </div>

      <div class="md-layout">
        <template v-if="tourType == 'tour'">
          <div class="md-layout-item md-size-100">
            <h6>Group Size</h6>
          </div>
          <div class="md-layout-item md-size-50">
            <md-field>
              <md-icon
                ><i class="fas fa-user-minus text-primary hi-price-icon"></i
              ></md-icon>
              <label>Min Group Size</label>
              <md-input
                :readonly="!canEdit"
                type="number"
                name="min_passengers"
                v-model="min_group_size"
              ></md-input>
            </md-field>
          </div>
          <div class="md-layout-item md-size-50">
            <md-field>
              <md-icon
                ><i class="fas fa-user-plus text-primary hi-price-icon"></i
              ></md-icon>
              <label>Max Group Size</label>
              <md-input
                :readonly="!canEdit"
                type="number"
                name="max_passengers"
                v-model="max_group_size"
              ></md-input>
            </md-field>
          </div>
        </template>

        <div class="md-layout-item md-size-100">
          <md-switch v-model="is_public" :disabled="!canEdit">Public</md-switch>
        </div>

        <div class="md-layout-item md-size-100">
          <div class="md-caption">Created On</div>
          <div class="">{{ created_at | formatDate }}</div>
        </div>

        <div v-if="canEdit" class="md-layout-item md-size-100">
          <md-button
            class="md-raised md-dense md-success mt-4"
            @click.prevent="updateDeparture"
            >Update</md-button
          >
        </div>
      </div>
    </md-card-content>
  </md-card>
</template>

<script>
import { createHelpers } from "vuex-map-fields";

const { mapFields } = createHelpers({
  getterType: "departures/getDepartureField",
  mutationType: "departures/updateDepartureField"
});

export default {
  props: {
    tour: [String, Number]
  },

  data() {
    return {};
  },

  computed: {
    ...mapFields({
      start_date: "departure.start_date",
      end_date: "departure.end_date",
      min_group_size: "departure.min_group_size",
      max_group_size: "departure.max_group_size",
      price_sync: "departure.price_sync",
      created_at: "departure.created_at",
      is_public: "departure.public",
      gbp: "departure.prices.gbp",
      eur: "departure.prices.eur",
      usd: "departure.prices.usd",
      tour_id: "departure.tour.id",
      tour_title: "departure.tour.title",
      tour_type: "departure.tour.type.value"
    }),

    canEdit() {
      return (
        this.$allowed(["departure.manage", "departure.update"]) ||
        (this.isSupplier && this.$allowed(["departure.update.own"]))
      );
    },

    departure() {
      return this.$store.state.departures.departure;
    },

    isSupplier() {
      return (
        this.departure?.tour?.supplier.id ==
        this.$store.state.auth.user.supplier.id
      );
    },

    tourTypeDescription() {
      return this.departure?.tour?.type?.value == "tour"
        ? "a Tour"
        : "an Event";
    },

    tourType() {
      return this.departure?.tour?.type?.value == "tour" ? "tour" : "event";
    }
  },

  methods: {
    updateDeparture() {
      this.$store
        .dispatch("departures/updateDeparture", {
          tourId: this.tour,
          departure: this.departure
        })
        .then(() => {
          this.$notify({
            timeout: 2500,
            message: "The Departure has been updated.",
            icon: "thumb_up",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "success"
          });
        });
    }
  }
};
</script>
